import React from 'react';
import styled from 'styled-components';
import { colors, text } from '@annote/core/styles';
import { Note } from '@annote/types';
import { createPortal } from 'react-dom';

export interface MenuProps {
  note: Note;
  closeMenu?: () => void;
  triggerRef?: React.RefObject<HTMLElement>;
}

const MenuContainerStyled = styled.div`
  position: fixed;
  background: white;
  border-radius: 8px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  padding: 8px 0;
  min-width: 200px;
  z-index: 99999;
  pointer-events: auto;
`;

export const MenuContainer: React.FC<{
  children: React.ReactNode;
  triggerRef: React.RefObject<HTMLElement>;
}> = ({ children, triggerRef }): React.ReactElement => {
  const [position, setPosition] = React.useState({ top: 0, left: 0 });

  React.useEffect(() => {
    if (!triggerRef.current) return undefined;

    const updatePosition = (): void => {
      if (!triggerRef.current) return;

      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
    };

    updatePosition();

    // Update position on scroll/resize
    window.addEventListener('scroll', updatePosition);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [triggerRef]);

  return createPortal(
    <MenuContainerStyled style={{ top: position.top, left: position.left }} data-portal-menu="true">
      {children}
    </MenuContainerStyled>,
    document.body,
  );
};

export const MenuItem = styled.div<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  color: ${(props) => (props.disabled ? colors.text.placeholder : colors.text.primary)};
  font-size: 14px;
  gap: 12px;
  &:hover {
    background-color: ${(props) => (props.disabled ? 'transparent' : colors.bg.surface)};
  }
`;

export const MenuInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  padding-right: 40px;
  border: 1px solid ${colors.borders.input.primary};
  border-radius: 4px;
  font-family: ${text.family.primary};
  font-size: ${text.size.primary};
  color: ${colors.text.primary};

  &::placeholder {
    color: ${colors.text.placeholder};
  }

  &:hover {
    border-color: ${colors.borders.input.selected};
  }

  &:focus {
    outline: none;
    border-color: ${colors.borders.input.selected};
  }
`;
